import { useEffect, useState } from 'react';
import { Wallet, AlertTriangle } from 'lucide-react';
import { mitgliedRechnungenAPI } from '@/lib/api';
import { useUmsatz } from '@/components/UmsatzSperre';

/* Finanzkarte am Kunden (03.11.2026, Betreiber: „Ich soll sie sehen“): offen und ob es in den nächsten Einzug kann, Mandat,
   Beiträge, Rücklastschriften, Vorgemerktes, offene Kontoänderung. Nur der Inhaber — der Server prüft es auch
   (rechnungen/finanzkarte.py), die Karte rechnet nichts selbst. Ohne Finanzdaten erscheint sie nicht. */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }));
const datum = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00`).toLocaleDateString('de-DE') : '—');
const KATEGORIE = {
  offen: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  ruecklastschrift: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  mandat_fehlt: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  nicht_eingezogen: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  im_einzug: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  zurueckgestellt: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  nur_lexware: 'bg-muted text-muted-foreground',
};
const ZAHLWEISE = { sepa: 'Lastschrift', bar: 'bar', ueberweisung: 'Überweisung', vorkasse: 'Vorkasse' };

function Abschnitt({ titel, children }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{titel}</p>
      {children}
    </div>
  );
}

export default function FinanzKarte({ leadId, onRechnungen }) {
  const { inhaber } = useUmsatz();
  const [k, setK] = useState(null);

  useEffect(() => {
    setK(null);
    if (!inhaber) return;
    mitgliedRechnungenAPI.finanzkarte(leadId).then((r) => setK(r.data)).catch(() => setK(null));
  }, [leadId, inhaber]);

  if (!inhaber || !k) return null;
  const leer = !k.offen.posten.length && !k.mandate.length && !k.beitraege.length && !k.ruecklastschriften.gesamt
    && !k.vorgemerkt.length && !k.kontoaenderungen.length && !k.rechnungen_gesamt;
  if (leer) return null;

  const gueltig = k.mandate.filter((m) => m.gueltig);
  const ne = k.naechster_einzug;
  const rl = k.ruecklastschriften;
  const lastschrift = k.beitraege.some((b) => b.zahlweise === 'sepa');

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 space-y-4" data-testid="finanz-karte">
      <div className="flex items-center gap-2">
        <Wallet className="w-4 h-4 text-muted-foreground" />
        <p className="text-sm font-semibold">Finanzen</p>
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">nur für dich</span>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className={`text-base font-bold tabular-nums leading-tight ${k.offen.summe > 0 ? 'text-amber-700 dark:text-amber-300' : ''}`}>{euro(k.offen.summe)}</div>
          <div className="text-[10px] text-muted-foreground">offen ({k.offen.anzahl})</div>
        </div>
        <div>
          <div className="text-base font-bold tabular-nums leading-tight">{ne.anzahl ? euro(ne.summe) : '—'}</div>
          <div className="text-[10px] text-muted-foreground">Einzug {datum(ne.einzugstag)}</div>
        </div>
        <div>
          <div className={`text-base font-bold tabular-nums leading-tight ${rl.letzte_12_monate ? 'text-red-600 dark:text-red-300' : ''}`}>{rl.letzte_12_monate}</div>
          <div className="text-[10px] text-muted-foreground">Rücklastschriften 12 Mon.</div>
        </div>
      </div>

      {k.offen.posten.length > 0 && (
        <Abschnitt titel="Offene Rechnungen">
          <ul className="space-y-1.5" data-testid="finanz-offen">
            {k.offen.posten.map((p) => (
              <li key={p.id} className="text-sm">
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    {p.titel || 'Rechnung'}
                    {p.nummer && <span className="block text-xs text-muted-foreground whitespace-nowrap">{p.nummer}</span>}
                  </span>
                  <span className="tabular-nums font-medium">{euro(p.betrag)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${KATEGORIE[p.kategorie] || 'bg-muted'}`}>{p.kategorie_text}</span>
                  {p.naechster_einzug && p.kategorie !== 'offen' && <span>kann in den nächsten Einzug</span>}
                  {p.fremder_zahler && <span>zahlt {p.zahler_name || 'jemand anderes'}</span>}
                  {p.fuer?.length > 0 && <span>für {p.fuer.join(', ')}</span>}
                  {p.grund && <span>{p.grund}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Abschnitt>
      )}

      <Abschnitt titel="Mandat">
        {gueltig.length === 0 ? (
          <p className={`text-sm ${lastschrift ? 'text-red-600 dark:text-red-300' : 'text-muted-foreground'}`}>
            {lastschrift ? 'Kein gültiges Mandat, obwohl per Lastschrift gezahlt wird.' : 'Kein gültiges Mandat.'}
          </p>
        ) : gueltig.map((m) => (
          <p key={m.mandatsreferenz} className="text-sm">
            <span className="font-medium">{m.mandatsreferenz}</span>
            <span className="block text-muted-foreground">{m.iban_maskiert || 'ohne IBAN'}{m.kreditinstitut ? ` · ${m.kreditinstitut}` : ''}</span>
            <span className="block text-xs text-muted-foreground">
              seit {datum(m.erteilt_am)}{m.letzte_nutzung_am ? ` · zuletzt genutzt ${datum(m.letzte_nutzung_am)}` : ' · noch nie genutzt'}
              {m.herkunft === 'sticky_import' ? ' · aus Sticky' : ''}
              {m.zahler_name && m.kontoinhaber && m.kontoinhaber !== m.zahler_name ? ` · Kontoinhaber ${m.kontoinhaber}` : ''}
            </span>
          </p>
        ))}
        {k.mandate.filter((m) => !m.gueltig).map((m) => (
          <p key={m.mandatsreferenz} className="text-xs text-muted-foreground">{m.mandatsreferenz} {m.status}{m.widerrufen_am ? ` am ${datum(m.widerrufen_am)}` : ''}</p>
        ))}
      </Abschnitt>

      {k.beitraege.length > 0 && (
        <Abschnitt titel="Laufende Beiträge">
          {k.beitraege.map((b) => (
            <div key={b.id} className="flex items-start gap-2 text-sm">
              <span className="min-w-0 flex-1">
                {b.tarif || 'Mitgliedschaft'}{b.status === 'paused' ? ' · pausiert' : ''}
                {k.beitraege.some((x) => x.member_id !== b.member_id) && <span className="block text-xs text-muted-foreground">{b.name}</span>}
              </span>
              <span className="text-right">
                <span className="block tabular-nums">{euro(b.monatlich)}</span>
                <span className="block text-xs text-muted-foreground">{ZAHLWEISE[b.zahlweise] || b.zahlweise}</span>
              </span>
            </div>
          ))}
        </Abschnitt>
      )}

      {rl.gesamt > 0 && (
        <Abschnitt titel={`Rücklastschriften (${rl.gesamt} insgesamt)`}>
          {rl.liste.map((r, i) => (
            <p key={`${r.am}-${i}`} className="text-xs text-muted-foreground">
              {datum(r.am)} · {euro(r.betrag)}{r.gebuehr ? ` + ${euro(r.gebuehr)} Gebühr` : ''}{r.grund ? ` · ${r.grund}` : ''}{r.rechnungsnummer ? ` · ${r.rechnungsnummer}` : ''}
            </p>
          ))}
        </Abschnitt>
      )}

      {(k.vorgemerkt.length > 0 || k.kontoaenderungen.length > 0) && (
        <Abschnitt titel="Vorgemerkt">
          {k.vorgemerkt.map((v) => (
            <p key={v.id} className="text-sm">{v.beschreibung} <span className="text-xs text-muted-foreground">· {v.stand_text}</span></p>
          ))}
          {k.kontoaenderungen.map((x) => (
            <p key={x.id} className="text-sm">Kontoänderung <span className="text-xs text-muted-foreground">· {x.stand_text}</span></p>
          ))}
        </Abschnitt>
      )}

      {k.hinweise.map((h) => (
        <p key={h} className="flex gap-1.5 text-xs text-amber-700 dark:text-amber-300"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{h}</p>
      ))}

      {k.rechnungen_gesamt > 0 && onRechnungen && (
        <button type="button" onClick={onRechnungen} className="text-xs font-semibold text-marke hover:underline">
          Alle {k.rechnungen_gesamt} Rechnungen ansehen
        </button>
      )}
    </div>
  );
}
